import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, ArrowLeft, Loader2, Shield, CheckCircle, AlertCircle } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { STORAGE_KEYS } from '../../../config/constants';
import { stashCodenamesPreload } from '../../reports/lib/portfolioOptions';

const NAVY = '#0A0A0A';
const NAVY_DEEP = '#000000';
const NAVY_SOFT = '#141414';
const CREAM = '#FFFFFF';
const GOLD = '#FFFFFF';
const HAIRLINE = 'rgba(255, 255, 255, 0.10)';
const HAIRLINE_STRONG = 'rgba(255, 255, 255, 0.22)';

const Wordmark = () => (
  <span
    className="font-display font-medium tracking-tight text-xl"
    style={{ color: CREAM }}
  >
    Banyan
    <span
      className="inline-block align-top"
      style={{
        width: 6,
        height: 6,
        borderRadius: 9999,
        background: GOLD,
        marginLeft: 2,
        marginTop: '0.55em',
      }}
    />
  </span>
);

const FieldLabel = ({ children }) => (
  <label
    className="block text-[13px] font-medium uppercase mb-2"
    style={{ letterSpacing: '0.14em', color: 'rgba(255,255,255,0.50)' }}
  >
    {children}
  </label>
);

const inputBase = {
  background: 'rgba(255,255,255,0.06)',
  border: `1px solid ${HAIRLINE_STRONG}`,
  color: CREAM,
  borderRadius: 6,
  width: '100%',
  padding: '0.75rem 1rem',
  fontSize: '0.9375rem',
  outline: 'none',
  transition: 'border-color 0.15s',
};

// ─── VIEW: Sign In Form ──────────────────────────────────────────────────────

const LoginView = ({ onLogin, onForgotPassword, error, inactivityMessage }) => {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    await onLogin({ email: identifier, password });
    setLoading(false);
  };

  return (
    <div>
      <h1
        className="font-display font-normal mb-2"
        style={{ fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)', lineHeight: 1.1, letterSpacing: '-0.02em', color: CREAM }}
      >
        Welcome back.
      </h1>
      <p className="mb-8 text-[16px]" style={{ color: 'rgba(255,255,255,0.50)' }}>
        Sign in to your Banyan account.
      </p>

      {inactivityMessage && (
        <div
          className="mb-6 px-4 py-3 rounded-md flex items-center gap-3 text-[14px]"
          style={{ background: 'rgba(255,255,255,0.08)', border: `1px solid rgba(255,255,255,0.25)`, color: GOLD }}
        >
          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {inactivityMessage}
        </div>
      )}

      {error && (
        <div
          className="mb-6 px-4 py-3 rounded-md flex items-center gap-3 text-[14px]"
          style={{ background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.25)', color: '#fca5a5' }}
        >
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <FieldLabel>Username or Email</FieldLabel>
          <input
            type="text"
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            onFocus={() => setFocusedField('id')}
            onBlur={() => setFocusedField(null)}
            placeholder="you@example.com"
            required
            disabled={loading}
            style={{
              ...inputBase,
              borderColor: focusedField === 'id' ? GOLD : HAIRLINE_STRONG,
            }}
          />
        </div>

        <div>
          <FieldLabel>Password</FieldLabel>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onFocus={() => setFocusedField('pw')}
              onBlur={() => setFocusedField(null)}
              placeholder="••••••••••••"
              required
              disabled={loading}
              style={{
                ...inputBase,
                borderColor: focusedField === 'pw' ? GOLD : HAIRLINE_STRONG,
                paddingRight: '3rem',
              }}
            />
            <button
              type="button"
              tabIndex={-1}
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 transition-opacity hover:opacity-100"
              style={{ color: 'rgba(255,255,255,0.35)' }}
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>

        <div className="flex justify-end">
          <button
            type="button"
            onClick={onForgotPassword}
            className="text-[14px] transition-colors"
            style={{ color: 'rgba(255,255,255,0.45)' }}
            onMouseEnter={(e) => (e.currentTarget.style.color = GOLD)}
            onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(255,255,255,0.45)')}
          >
            Forgot password?
          </button>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full flex items-center justify-center gap-2 font-medium text-[15px] py-3.5 rounded-full transition-all disabled:opacity-50"
          style={{ background: CREAM, color: NAVY }}
          onMouseEnter={(e) => {
            if (!loading) e.currentTarget.style.boxShadow = '0 14px 40px -12px rgba(255,255,255,0.20)';
          }}
          onMouseLeave={(e) => (e.currentTarget.style.boxShadow = 'none')}
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          {loading ? 'Signing in…' : 'Sign in →'}
        </button>
      </form>
    </div>
  );
};

// ─── VIEW: Enter emailed login code ──────────────────────────────────────────

const VerifyLoginView = ({ email, onVerify, onBack }) => {
  const [code, setCode] = useState('');
  const [rememberDevice, setRememberDevice] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [focused, setFocused] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (code.length !== 6) return;
    setLoading(true);
    setError('');
    const result = await onVerify({ code, rememberDevice });
    // On success the parent redirects (handleLoginSuccess), so we only need to
    // surface an error here; keep the spinner up while the redirect happens.
    if (!result.success) {
      setError(result.error || 'Invalid or expired code.');
      setLoading(false);
    }
  };

  return (
    <div className="text-center">
      <div
        className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6"
        style={{ background: 'rgba(255,255,255,0.08)', border: `1px solid rgba(255,255,255,0.18)` }}
      >
        <Shield className="w-7 h-7" style={{ color: GOLD }} />
      </div>

      <h1
        className="font-display font-normal mb-3"
        style={{ fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)', lineHeight: 1.1, letterSpacing: '-0.02em', color: CREAM }}
      >
        Enter your code.
      </h1>
      <p className="text-[16px] mb-1" style={{ color: 'rgba(255,255,255,0.50)' }}>
        We sent a 6-digit code to
      </p>
      <p className="font-medium mb-7" style={{ color: CREAM }}>
        {email}
      </p>

      {error && (
        <div
          className="mb-5 px-4 py-3 rounded-md flex items-center justify-center gap-2 text-[14px]"
          style={{ background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.25)', color: '#fca5a5' }}
        >
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5 text-left">
        <div>
          <FieldLabel>Verification code</FieldLabel>
          <input
            type="text"
            inputMode="numeric"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            placeholder="000000"
            maxLength={6}
            autoComplete="one-time-code"
            autoFocus
            required
            disabled={loading}
            style={{
              ...inputBase,
              borderColor: focused ? GOLD : HAIRLINE_STRONG,
              textAlign: 'center',
              fontSize: '1.5rem',
              letterSpacing: '0.4em',
              fontFamily: 'monospace',
            }}
          />
        </div>
        <label
          className="flex items-center gap-2.5 text-[14px] cursor-pointer select-none"
          style={{ color: 'rgba(255,255,255,0.55)' }}
        >
          <input
            type="checkbox"
            checked={rememberDevice}
            onChange={(e) => setRememberDevice(e.target.checked)}
            disabled={loading}
            className="w-4 h-4 cursor-pointer"
            style={{ accentColor: GOLD }}
          />
          Trust this device for 30 days
        </label>
        <div className="flex gap-3 pt-1">
          <button
            type="button"
            onClick={onBack}
            disabled={loading}
            className="flex-1 flex items-center justify-center gap-2 text-[14px] py-3 rounded-full transition-all"
            style={{ color: CREAM, border: `1px solid ${HAIRLINE_STRONG}` }}
            onMouseEnter={(e) => (e.currentTarget.style.borderColor = GOLD)}
            onMouseLeave={(e) => (e.currentTarget.style.borderColor = HAIRLINE_STRONG)}
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back
          </button>
          <button
            type="submit"
            disabled={loading || code.length !== 6}
            className="flex-1 text-[14px] py-3 rounded-full font-medium transition-all disabled:opacity-50"
            style={{ background: CREAM, color: NAVY }}
          >
            {loading ? 'Verifying…' : 'Sign in'}
          </button>
        </div>
      </form>

      <p className="text-[14px] mt-7" style={{ color: 'rgba(255,255,255,0.35)' }}>
        The code expires in 15 minutes. Don't see it? Check your spam folder.
      </p>
    </div>
  );
};

// ─── VIEW: Forgot password (3 steps inline) ──────────────────────────────────

const ForgotPasswordView = ({ onRequestReset, onVerifyCode, onResetPassword, onBack }) => {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [focusedField, setFocusedField] = useState(null);

  const stepIcons = [Mail, Shield, Lock];
  const StepIcon = stepIcons[step - 1];

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const result = await onRequestReset({ email });
    setLoading(false);
    if (result.success) {
      setMessage(result.message || 'Verification code sent.');
      setStep(2);
    } else {
      setError(result.error || 'Failed to send verification code.');
    }
  };

  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const result = await onVerifyCode({ email, code });
    setLoading(false);
    if (result.success) {
      setMessage('Code verified.');
      setStep(3);
    } else {
      setError(result.error || 'Invalid or expired code.');
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) return;
    setLoading(true);
    setError('');
    const result = await onResetPassword({ email, code, newPassword });
    setLoading(false);
    if (result.success) {
      setMessage('Password reset. Returning to sign in…');
    } else {
      setError(result.error || 'Failed to reset password.');
    }
  };

  return (
    <div>
      {/* Step indicator */}
      <div className="flex items-center gap-2 mb-8">
        {[1, 2, 3].map((n) => (
          <React.Fragment key={n}>
            <div
              className="w-7 h-7 rounded-full flex items-center justify-center text-[13px] font-medium transition-all"
              style={{
                background: step >= n ? GOLD : 'rgba(255,255,255,0.08)',
                color: step >= n ? NAVY : 'rgba(255,255,255,0.35)',
              }}
            >
              {step > n ? <CheckCircle className="w-3.5 h-3.5" /> : n}
            </div>
            {n < 3 && (
              <div
                className="flex-1 h-px transition-all"
                style={{ background: step > n ? GOLD : HAIRLINE }}
              />
            )}
          </React.Fragment>
        ))}
      </div>

      <div
        className="w-10 h-10 rounded-full flex items-center justify-center mb-5"
        style={{ background: 'rgba(255,255,255,0.08)', border: `1px solid rgba(255,255,255,0.18)` }}
      >
        <StepIcon className="w-5 h-5" style={{ color: GOLD }} />
      </div>

      <h1
        className="font-display font-normal mb-2"
        style={{ fontSize: 'clamp(1.5rem, 3vw, 2.25rem)', lineHeight: 1.1, letterSpacing: '-0.02em', color: CREAM }}
      >
        Reset password
      </h1>
      <p className="text-[15px] mb-7" style={{ color: 'rgba(255,255,255,0.45)' }}>
        {step === 1 && 'Enter your email to receive a verification code.'}
        {step === 2 && `Enter the 8-character code sent to ${email}.`}
        {step === 3 && 'Create a new password for your account.'}
      </p>

      {(error || message) && (
        <div
          className="mb-5 px-4 py-3 rounded-md flex items-center gap-3 text-[14px]"
          style={
            error
              ? { background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.25)', color: '#fca5a5' }
              : { background: 'rgba(255,255,255,0.05)', border: `1px solid rgba(255,255,255,0.18)`, color: GOLD }
          }
        >
          {error ? <AlertCircle className="w-4 h-4 flex-shrink-0" /> : <CheckCircle className="w-4 h-4 flex-shrink-0" />}
          {error || message}
        </div>
      )}

      {/* Step 1 */}
      {step === 1 && (
        <form onSubmit={handleEmailSubmit} className="space-y-5">
          <div>
            <FieldLabel>Email address</FieldLabel>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onFocus={() => setFocusedField('email')}
              onBlur={() => setFocusedField(null)}
              placeholder="you@example.com"
              required
              disabled={loading}
              style={{ ...inputBase, borderColor: focusedField === 'email' ? GOLD : HAIRLINE_STRONG }}
            />
          </div>
          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={onBack}
              disabled={loading}
              className="flex-1 flex items-center justify-center gap-2 text-[14px] py-3 rounded-full transition-all"
              style={{ color: CREAM, border: `1px solid ${HAIRLINE_STRONG}` }}
              onMouseEnter={(e) => (e.currentTarget.style.borderColor = GOLD)}
              onMouseLeave={(e) => (e.currentTarget.style.borderColor = HAIRLINE_STRONG)}
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 text-[14px] py-3 rounded-full font-medium transition-all disabled:opacity-50"
              style={{ background: CREAM, color: NAVY }}
            >
              {loading ? 'Sending…' : 'Send code'}
            </button>
          </div>
        </form>
      )}

      {/* Step 2 */}
      {step === 2 && (
        <form onSubmit={handleCodeSubmit} className="space-y-5">
          <div>
            <FieldLabel>Verification code</FieldLabel>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/[^A-Za-z0-9_-]/g, '').slice(0, 8))}
              onFocus={() => setFocusedField('code')}
              onBlur={() => setFocusedField(null)}
              placeholder="XXXXXXXX"
              maxLength={8}
              autoComplete="one-time-code"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              required
              disabled={loading}
              style={{
                ...inputBase,
                borderColor: focusedField === 'code' ? GOLD : HAIRLINE_STRONG,
                textAlign: 'center',
                fontSize: '1.5rem',
                letterSpacing: '0.3em',
                fontFamily: 'monospace',
              }}
            />
          </div>
          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={() => { setStep(1); setCode(''); setError(''); setMessage(''); }}
              disabled={loading}
              className="flex-1 flex items-center justify-center gap-2 text-[14px] py-3 rounded-full transition-all"
              style={{ color: CREAM, border: `1px solid ${HAIRLINE_STRONG}` }}
              onMouseEnter={(e) => (e.currentTarget.style.borderColor = GOLD)}
              onMouseLeave={(e) => (e.currentTarget.style.borderColor = HAIRLINE_STRONG)}
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back
            </button>
            <button
              type="submit"
              disabled={loading || code.length !== 8}
              className="flex-1 text-[14px] py-3 rounded-full font-medium transition-all disabled:opacity-50"
              style={{ background: CREAM, color: NAVY }}
            >
              {loading ? 'Verifying…' : 'Verify code'}
            </button>
          </div>
        </form>
      )}

      {/* Step 3 */}
      {step === 3 && (
        <form onSubmit={handlePasswordSubmit} className="space-y-5">
          <div>
            <FieldLabel>New password</FieldLabel>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                onFocus={() => setFocusedField('np')}
                onBlur={() => setFocusedField(null)}
                placeholder="Min. 12 characters"
                required
                disabled={loading}
                style={{ ...inputBase, borderColor: focusedField === 'np' ? GOLD : HAIRLINE_STRONG, paddingRight: '3rem' }}
              />
              <button
                type="button"
                tabIndex={-1}
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2"
                style={{ color: 'rgba(255,255,255,0.35)' }}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="mt-1.5 text-[13px]" style={{ color: 'rgba(255,255,255,0.30)' }}>
              Uppercase, lowercase, number, and special character required.
            </p>
          </div>
          <div>
            <FieldLabel>Confirm password</FieldLabel>
            <div className="relative">
              <input
                type={showConfirm ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onFocus={() => setFocusedField('cp')}
                onBlur={() => setFocusedField(null)}
                placeholder="Re-enter password"
                required
                disabled={loading}
                style={{ ...inputBase, borderColor: focusedField === 'cp' ? GOLD : HAIRLINE_STRONG, paddingRight: '3rem' }}
              />
              <button
                type="button"
                tabIndex={-1}
                onClick={() => setShowConfirm((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2"
                style={{ color: 'rgba(255,255,255,0.35)' }}
              >
                {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {newPassword && confirmPassword && newPassword !== confirmPassword && (
              <p className="mt-1.5 text-[13px]" style={{ color: '#fca5a5' }}>Passwords do not match.</p>
            )}
          </div>
          <button
            type="submit"
            disabled={loading || newPassword !== confirmPassword || newPassword.length < 12}
            className="w-full text-[15px] py-3.5 rounded-full font-medium transition-all disabled:opacity-50"
            style={{ background: CREAM, color: NAVY }}
          >
            {loading ? 'Resetting…' : 'Reset password'}
          </button>
        </form>
      )}
    </div>
  );
};

// ─── Main page ────────────────────────────────────────────────────────────────

const SignInPage = () => {
  const { login, verifyLoginCode, forgotPassword, verifyResetCode, resetPassword, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [view, setView] = useState('login'); // 'login' | 'verify' | 'forgotPassword'
  const [loginError, setLoginError] = useState('');
  const [waitingEmail, setWaitingEmail] = useState('');
  const [inactivityMessage, setInactivityMessage] = useState('');

  // Handle login completion: the refresh token is in the HttpOnly
  // `banyan_refresh` cookie that the verify-login-code response set on this
  // origin. We only persist the short-lived access token + its expiry here.
  const handleLoginSuccess = useCallback((data) => {
    localStorage.removeItem(STORAGE_KEYS.LAST_ACTIVITY);
    sessionStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, data.access_token);
    if (data.expires_at) {
      sessionStorage.setItem(STORAGE_KEYS.TOKEN_EXPIRES_AT, data.expires_at);
    }
    // Carry the login-bundled /codenames payload across the hard reload below so
    // the Portfolios list + report pickers render with zero post-login fetch
    // (portfolioOptions seeds its cache from it on the next module load).
    stashCodenamesPreload(data.codenames);
    localStorage.setItem(STORAGE_KEYS.LAST_ACTIVITY, Date.now().toString());
    window.location.href = '/';
  }, []);

  // Inactivity message
  useEffect(() => {
    const reason = searchParams.get('reason');
    if (reason === 'inactivity') {
      setInactivityMessage('Your session expired due to inactivity. Please sign in again.');
      searchParams.delete('reason');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) navigate('/');
  }, [isAuthenticated, navigate]);

  const handleLogin = async (data) => {
    setLoginError('');
    const result = await login(data);
    if (result.success && result.trustedDevice) {
      // Trusted device: the backend verified the password, recognised the
      // banyan_device cookie, and returned the session directly — skip the code.
      handleLoginSuccess(result.data);
      return;
    }
    if (result.success) {
      setWaitingEmail(result.email);
      setView('verify');
    } else {
      setLoginError(result.error || 'Login failed.');
    }
  };

  const handleVerifyLoginCode = async ({ code, rememberDevice }) => {
    const result = await verifyLoginCode({ email: waitingEmail, code, rememberDevice });
    if (result.success) {
      handleLoginSuccess(result.data); // stores access token + redirects
    }
    return result;
  };

  const handleForgotPassword = async ({ email }) => {
    const result = await forgotPassword({ email });
    return result;
  };

  const handleVerifyCode = async ({ email, code }) => {
    return await verifyResetCode({ email, code });
  };

  const handleResetPassword = async ({ email, code, newPassword }) => {
    const result = await resetPassword({ email, code, newPassword });
    if (result.success) {
      setTimeout(() => setView('login'), 2000);
    }
    return result;
  };

  return (
    <div
      className="relative min-h-screen font-sans-body overflow-hidden"
      style={{ background: NAVY, color: CREAM }}
    >
      {/* Ambient gradients */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background: `radial-gradient(1200px 600px at 85% -10%, rgba(255,255,255,0.03), transparent 60%),
                       radial-gradient(900px 500px at 10% 110%, rgba(255,255,255,0.03), transparent 60%),
                       linear-gradient(180deg, ${NAVY_DEEP} 0%, ${NAVY} 45%, ${NAVY} 100%)`,
        }}
      />
      <div aria-hidden className="banyan-grain absolute inset-0 pointer-events-none" />

      {/* Header */}
      <header className="relative z-10 h-16 flex items-center px-6 md:px-10 justify-between"
        style={{ borderBottom: `1px solid ${HAIRLINE}` }}
      >
        <button onClick={() => navigate('/')} className="transition-opacity hover:opacity-80">
          <Wordmark />
        </button>
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-[14px] transition-colors"
          style={{ color: 'rgba(255,255,255,0.45)' }}
          onMouseEnter={(e) => (e.currentTarget.style.color = CREAM)}
          onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(255,255,255,0.45)')}
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Back to home
        </button>
      </header>

      {/* Content */}
      <main className="relative z-10 flex items-center justify-center min-h-[calc(100vh-4rem)] px-6 py-16">
        <div className="w-full max-w-sm">
          {/* Card */}
          <div
            className="rounded-sm p-8 md:p-10"
            style={{
              background: `linear-gradient(145deg, ${NAVY_SOFT} 0%, ${NAVY} 100%)`,
              border: `1px solid ${HAIRLINE}`,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Gold top accent */}
            <div
              aria-hidden
              className="absolute top-0 left-0 h-[1px] w-full"
              style={{
                background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)`,
                opacity: 0.5,
              }}
            />

            {view === 'login' && (
              <LoginView
                onLogin={handleLogin}
                onForgotPassword={() => setView('forgotPassword')}
                error={loginError}
                inactivityMessage={inactivityMessage}
              />
            )}
            {view === 'verify' && (
              <VerifyLoginView
                email={waitingEmail}
                onVerify={handleVerifyLoginCode}
                onBack={() => { setWaitingEmail(''); setView('login'); }}
              />
            )}
            {view === 'forgotPassword' && (
              <ForgotPasswordView
                onRequestReset={handleForgotPassword}
                onVerifyCode={handleVerifyCode}
                onResetPassword={handleResetPassword}
                onBack={() => setView('login')}
              />
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default SignInPage;
